import IconButton from '@/global/components/Button/IconButton'
import Skeleton from '@/global/components/Skeleton'
import { Tooltip } from '@/global/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { CategoriaEnum } from '@acme/auth'
import { OrigensEnum, parserRegisterOrigin } from '@acme/common'
import { StatusAprovacao } from '@acme/core'
import CircularProgress from '@/global/components/CircularProgress'
import InputAdornment from '@mui/material/InputAdornment'
import OutlinedInput from '@mui/material/OutlinedInput'
import dayjs from 'dayjs'
import { CleaveTextField } from 'global/components/Form/CleaveTextField'
import { useAuth } from 'hooks'
import {
    CircleArrowLeftIcon,
    CircleArrowRightIcon,
    HourglassIcon,
    LockKeyholeIcon,
    OctagonAlertIcon,
    PencilIcon,
    TriangleAlertIcon,
    XIcon,
} from 'lucide-react'
import React, { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { registros } from 'service/api'
import { toast } from 'sonner'
import { useStore } from 'store'
import ModalJustificativaRegistro from './modals/JustificativaRegistro'
import { useDigitacaoContext } from './pages/DigitacaoLayout'
import { useUpdateDigitacaoQueryData } from './pages/useDigitacaoData'
import { entradasSaidas } from './utils/helpers'

function origemParser(origem?: number) {
    return origem
        ? parserRegisterOrigin(origem)
        : { color: 'var(--border)', title: '' }
}

type InputProps = {
    dia: string
    funcionarioId: number
    entSai: string
    defaultValue?: string
    status?: keyof typeof StatusAprovacao
    origem?: number
    registroId?: number
    disabled?: boolean
    saidaDiaAnterior?: boolean
    entradaDiaSeguinte?: boolean
    diaRegistro?: Date
}

function makeInputId(
    view: string,
    dia: string,
    entSai: string,
    funcionarioId: number,
) {
    return view === 'mensal'
        ? `Registros_${dia}_${entSai}`
        : `Registros_${dia}_${entSai}_${funcionarioId}`
}

/**
 * Tooltip colorido para indicar status do registro
 */
function StatusTooltip({
    children,
    label,
    color,
}: {
    children: React.ReactNode
    label: string
    color: string
}) {
    return (
        <Tooltip
            style={
                {
                    borderColor: color,
                    '--bordClr': color,
                } as React.CSSProperties
            }
            title={label}
            className={cn(
                'border',
                '[&_[data-slot=tooltip-arrow]]:border-b',
                '[&_[data-slot=tooltip-arrow]]:border-r',
                '[&_[data-slot=tooltip-arrow]]:border-[var(--bordClr)]',
            )}
        >
            {children}
        </Tooltip>
    )
}

/**
 * Ícone de status derivado apenas das props persistidas (sem estado de
 * mutação). Compartilhado entre o input estático e o editor.
 */
function StatusIcon({
    status,
    saidaDiaAnterior,
    entradaDiaSeguinte,
    defaultValue,
    disabled,
    color,
    title,
}: {
    status?: keyof typeof StatusAprovacao
    saidaDiaAnterior?: boolean
    entradaDiaSeguinte?: boolean
    defaultValue?: string
    disabled?: boolean
    color: string
    title: string
}) {
    if (status) {
        if (saidaDiaAnterior) {
            return (
                <StatusTooltip
                    label={`${title} (Saída do dia anterior)`}
                    color={color}
                >
                    <div className="flex items-center p-0">
                        <CircleArrowLeftIcon
                            style={{ color }}
                            className="outlined size-5"
                        />
                    </div>
                </StatusTooltip>
            )
        }
        if (entradaDiaSeguinte) {
            return (
                <StatusTooltip
                    label={`${title} (Entrada do dia seguinte)`}
                    color={color}
                >
                    <div className="flex items-center p-0">
                        <CircleArrowRightIcon
                            style={{ color }}
                            className="outlined size-5"
                        />
                    </div>
                </StatusTooltip>
            )
        }

        switch (status) {
            case StatusAprovacao.APROVADO:
                return (
                    <StatusTooltip label={title} color={color}>
                        <div
                            className="size-4 rounded-full"
                            style={{ background: color }}
                        />
                    </StatusTooltip>
                )
            case StatusAprovacao.PENDENTE:
            case StatusAprovacao.PENDENTE_EXCLUSAO:
                return (
                    <StatusTooltip
                        label={`${title} (Aguardando aprovação)`}
                        color="var(--warning)"
                    >
                        <div className="flex items-center justify-center">
                            <HourglassIcon className="size-4 text-warning" />
                        </div>
                    </StatusTooltip>
                )
            case StatusAprovacao.REPROVADO:
                return (
                    <StatusTooltip
                        label={`${title} (Reprovado)`}
                        color="var(--error)"
                    >
                        <div className="flex items-center justify-center">
                            <OctagonAlertIcon className="size-4 text-error" />
                        </div>
                    </StatusTooltip>
                )
            default:
                break
        }
    }

    if (!defaultValue) {
        return disabled ? (
            <StatusTooltip label={`Edição bloqueada`} color="var(--border)">
                <div className="flex items-center justify-center">
                    <LockKeyholeIcon className="size-4 opacity-60" />
                </div>
            </StatusTooltip>
        ) : (
            <PencilIcon className="size-4 opacity-40" />
        )
    }

    return null
}

/**
 * Marcador de registro deslocado (saída/entrada pertencente a outro dia).
 * Compartilhado entre as três variantes de célula.
 */
function DiaDeslocadoBadge({
    diaRegistro,
    color,
}: {
    diaRegistro: Date
    color: string
}) {
    return (
        <StatusTooltip
            label={`Registro deslocado do dia ${dayjs
                .utc(diaRegistro)
                .format('DD/MM')}`}
            color={color}
        >
            <svg
                width="10"
                height="10"
                viewBox="0 0 10 10"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="absolute bottom-[1px] right-[1px] rounded-br-[3px] overflow-hidden size-5"
            >
                <path d="M0 10L10 10L10 0" fill={color} />
            </svg>
        </StatusTooltip>
    )
}

/**
 * Célula bloqueada para edição: nunca monta o editor. Reusa o StaticInput,
 * que já resolve tabIndex, cursor e ícone de cadeado quando `disabled`.
 */
function DisabledInput(props: InputProps) {
    return <StaticInput {...props} disabled />
}

/**
 * Célula leve renderizada por padrão: sem observer de mutação, sem cleave.js e
 * sem assinaturas de store. Mantém o mesmo `id`, foco e ícones para preservar
 * navegação por Tab, scroll por hash e clique-para-detalhes. Ao focar uma
 * célula vazia editável, o wrapper monta o editor completo.
 */
function StaticInput({
    dia,
    entSai,
    funcionarioId,
    defaultValue = '',
    status,
    origem,
    disabled,
    registroId,
    saidaDiaAnterior,
    entradaDiaSeguinte,
    diaRegistro,
    onActivate,
}: InputProps & { onActivate?: () => void }) {
    const { view } = useDigitacaoContext()
    const navigate = useNavigate()
    const { color, title } = origemParser(origem)

    const id = makeInputId(view, dia, entSai, funcionarioId)
    const isFilled = !!defaultValue
    const editable = !isFilled && !disabled
    const isDiaDeslocado =
        diaRegistro && !dayjs.utc(diaRegistro).isSame(dia, 'day')

    return (
        <div className="relative inline-flex w-25 min-w-23 items-center">
            <input
                id={id}
                name={entSai}
                readOnly
                tabIndex={editable ? undefined : -1}
                defaultValue={defaultValue}
                placeholder="--:--"
                autoComplete="off"
                onFocus={editable ? onActivate : undefined}
                onClick={
                    isFilled && registroId
                        ? () => navigate(`registros/${registroId}`)
                        : undefined
                }
                className={cn(
                    'h-10 w-full rounded-[4px] border px-3.5 text-base outline-none [font-family:inherit]',
                    'border-black/23 bg-transparent text-foreground dark:border-white/23',
                    'placeholder:text-foreground/42 dark:placeholder:text-foreground/50',
                    'hover:border-foreground focus:border-primary',
                )}
                style={{
                    textDecoration:
                        status === StatusAprovacao.REPROVADO
                            ? 'line-through'
                            : 'none',
                    cursor: isFilled
                        ? 'pointer'
                        : disabled
                          ? 'not-allowed'
                          : 'text',
                }}
            />
            <span className="absolute right-3.5 flex items-center">
                <StatusIcon
                    status={status}
                    saidaDiaAnterior={saidaDiaAnterior}
                    entradaDiaSeguinte={entradaDiaSeguinte}
                    defaultValue={defaultValue}
                    disabled={disabled}
                    color={color}
                    title={title}
                />
            </span>
            {isDiaDeslocado && (
                <DiaDeslocadoBadge diaRegistro={diaRegistro} color={color} />
            )}
        </div>
    )
}

/**
 * Input interativo completo (mutação de criação, justificativa, cleave.js).
 * Montado apenas quando a célula é engajada — ver {@link InputDigitacao}.
 */
function InputEditor({
    dia,
    entSai,
    defaultValue = '',
    status,
    origem,
    funcionarioId,
    registroId,
    disabled,
    entradaDiaSeguinte,
    saidaDiaAnterior,
    diaRegistro,
}: InputProps) {
    const {
        decoded: { categoria },
    } = useAuth()
    const { view } = useDigitacaoContext()
    const navigate = useNavigate()
    const empresaId = useStore(state => state.companyId)
    const justificativaPadrao = useStore(
        state => state.company?.justificativaPadraoRegistro,
    )
    const { setQueryData } = useUpdateDigitacaoQueryData()
    const { mutateAsync, isLoading, isError, error, reset } =
        registros.mutations.useCreate({
            queryParams: {
                throwOnDuplicate: true,
            },
            onSuccess: payload => {
                setQueryData('REGISTRO', payload)
                setExigirJustificativa(false)
                setTimeout(() => {
                    // Se o usuário já navegou para outra célula (Tab/Shift+Tab
                    // nativos), não rouba o foco de volta
                    const focado = document.activeElement as HTMLElement | null
                    if (
                        focado?.id?.startsWith('Registros_') &&
                        focado.id !== key
                    ) {
                        return
                    }
                    focusNextElement()
                }, 100)
            },
            onError: e => {
                toast.error(e.message)
            },
        })
    const [value, setValue] = React.useState(defaultValue)
    const [isHover, setIsHover] = React.useState(false)
    const [exigirJustificativa, setExigirJustificativa] = React.useState(false)
    const { color, title } = origemParser(origem)

    const focusNextElement = () => {
        // Encontrar o índice atual do entSai no array entradasSaidas
        const currentIndex = entradasSaidas.indexOf(entSai as any)

        if (currentIndex === -1) return

        // Tentar focar o próximo elemento na mesma linha primeiro
        for (let i = currentIndex + 1; i < entradasSaidas.length; i++) {
            const nextElementId =
                view === 'mensal'
                    ? `Registros_${dia}_${entradasSaidas[i]}`
                    : `Registros_${dia}_${entradasSaidas[i]}_${funcionarioId}`
            const nextElement = document.getElementById(
                nextElementId,
            ) as HTMLInputElement | null

            if (
                nextElement &&
                !nextElement.hasAttribute('disabled') &&
                !nextElement.value &&
                nextElement.tabIndex !== -1
            ) {
                nextElement.focus()
                return
            }
        }

        // Se não encontrou na mesma linha, tentar o primeiro elemento da próxima linha (próximo dia)
        // Isso só se aplica se estivermos na visualização mensal
        if (view === 'mensal') {
            const registrosContainer = document.getElementById(
                `Registros_${dia}`,
            )
            if (registrosContainer) {
                // Encontrar o próximo container de registros
                const currentRow = registrosContainer.closest(
                    '[data-index]',
                ) as HTMLElement
                if (currentRow) {
                    const currentRowIndex = parseInt(
                        currentRow.dataset.index || '0',
                    )
                    const nextRow = document.querySelector(
                        `[data-index="${currentRowIndex + 1}"]`,
                    )

                    if (nextRow) {
                        const firstInput = Array.from(
                            nextRow.querySelectorAll<HTMLInputElement>(
                                'input:not([disabled])',
                            ),
                        ).find(input => !input.value && input.tabIndex !== -1)
                        if (firstInput) {
                            firstInput.focus()
                            return
                        }
                    }
                }
            }
        }
    }

    const handleBlur = () => {
        if (isError || disabled || defaultValue) return
        if (value.length < 1) return
        if (!justificativaPadrao) setExigirJustificativa(true)
        else criarRegistro(justificativaPadrao)
    }

    async function criarRegistro(justificativa: string) {
        const hora = value.length === 1 ? `0${value}:00` : value.padEnd(5, '0')
        return mutateAsync({
            empresaId,
            funcionarioId,
            data: dia,
            origem: [
                CategoriaEnum.Cliente,
                CategoriaEnum.Supervisor,
                CategoriaEnum.Avaliacao,
            ].includes(categoria)
                ? OrigensEnum.MANUAL_SUPERVISOR
                : OrigensEnum.MANUAL_FUNCIONARIO,
            hora,
            justificativa,
        } as any)
    }

    const Icone = useMemo(() => {
        if (isLoading) return <CircularProgress size={14} />

        if (isError && !status) {
            return (
                <Tooltip
                    title={
                        (error?.message || 'Erro ao salvar registro') +
                        ' - Clique para limpar'
                    }
                >
                    <IconButton
                        size="small"
                        onMouseEnter={() => setIsHover(true)}
                        onMouseLeave={() => setIsHover(false)}
                        onClick={() => {
                            setValue('')
                            setIsHover(false)
                        }}
                        sx={{
                            width: 14,
                            p: 0,
                        }}
                        tabIndex={-1}
                    >
                        {isHover ? (
                            <XIcon className="size-4 text-error stroke-3" />
                        ) : (
                            <TriangleAlertIcon className="size-4 text-error stroke-3" />
                        )}
                    </IconButton>
                </Tooltip>
            )
        }

        return (
            <StatusIcon
                status={status}
                saidaDiaAnterior={saidaDiaAnterior}
                entradaDiaSeguinte={entradaDiaSeguinte}
                defaultValue={defaultValue}
                disabled={disabled}
                color={color}
                title={title}
            />
        )
    }, [
        defaultValue,
        isLoading,
        disabled,
        status,
        isError,
        isHover,
        saidaDiaAnterior,
        entradaDiaSeguinte,
        color,
        title,
    ])

    useEffect(() => {
        if (isError)
            document.getElementById(`Registros_${dia}_${entSai}`)?.focus()
    }, [isError])

    useEffect(() => {
        if (!value) reset()
    }, [value])

    useEffect(() => {
        setValue(defaultValue)
    }, [defaultValue])

    const isDiaDeslocado =
        diaRegistro && !dayjs.utc(diaRegistro).isSame(dia, 'day')

    const MemoizedModal = useMemo(() => {
        if (!exigirJustificativa) return null
        return (
            <ModalJustificativaRegistro
                key={`justificativa_${dia}_${entSai}`}
                onClose={() => {
                    setExigirJustificativa(false)
                    setValue('')
                    setTimeout(() => {
                        document
                            .getElementById(`Registros_${dia}_${entSai}`)
                            ?.focus()
                    }, 100)
                }}
                onSubmit={async justificativa => criarRegistro(justificativa)}
                isLoading={isLoading}
            />
        )
    }, [exigirJustificativa, isLoading])

    const key = useMemo(
        () => makeInputId(view, dia, entSai, funcionarioId),
        [dia, entSai, funcionarioId, view],
    )

    // `autoFocus` do MUI não chega ao input interno via CleaveTextField, então
    // focamos explicitamente ao montar (a célula só monta o editor ao ser
    // engajada, ver InputDigitacao).
    useEffect(() => {
        document.getElementById(key)?.focus()
    }, [key])

    return [
        <OutlinedInput
            id={key}
            key={key}
            name={entSai}
            size="small"
            value={value}
            onChange={event => setValue(event.target.value)}
            inputComponent={CleaveTextField as any}
            onClick={
                defaultValue && registroId
                    ? () => navigate(`registros/${registroId}`)
                    : undefined
            }
            onBlur={handleBlur}
            error={isError}
            placeholder="--:--"
            className="relative w-25 min-w-23 cursor-default"
            autoComplete="off"
            inputProps={{
                options: {
                    time: true,
                    timePattern: ['h', 'm'],
                },
                tabIndex: defaultValue || disabled ? -1 : undefined,
                style: {
                    textDecoration:
                        status === StatusAprovacao.REPROVADO
                            ? 'line-through'
                            : 'none',
                    cursor: defaultValue
                        ? 'pointer'
                        : disabled
                          ? 'not-allowed'
                          : 'text',
                },
            }}
            componentsProps={{
                input: {
                    readOnly: !!defaultValue || disabled,
                },
            }}
            endAdornment={[
                <InputAdornment
                    position="end"
                    className={cn({ 'cursor-pointer': !!defaultValue })}
                    key="icone"
                >
                    {Icone}
                </InputAdornment>,
                isDiaDeslocado && (
                    <DiaDeslocadoBadge
                        key="dia-deslocado"
                        diaRegistro={diaRegistro}
                        color={color}
                    />
                ),
            ]}
        />,
        MemoizedModal,
    ]
}

/**
 * Renderiza uma célula estática barata por padrão e só monta o editor completo
 * ({@link InputEditor}) quando o usuário engaja a célula. Isso evita montar
 * centenas de observers de mutação + instâncias de cleave.js de uma vez, que
 * travava a tela ao abrir a Digitação.
 */
export default function InputDigitacao(props: InputProps) {
    const [active, setActive] = React.useState(false)

    if (props.disabled) return <DisabledInput {...props} />

    if (active) return <InputEditor {...props} />

    return (
        <StaticInput
            {...props}
            key={props.registroId}
            onActivate={() => setActive(true)}
        />
    )
}

export function InputLoading() {
    return <Skeleton className="max-w-25 min-w-25 h-10 rounded-sm" />
}
